
import React, { useState } from 'react';
import { Menu, X, Search, Facebook, Instagram, Youtube } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();

  const navigation = [
    { name: 'Startseite', href: '/' },
    { name: 'Blog', href: '/blog' },
    { name: 'Training', href: '/training' },
    { name: 'Turniere', href: '/tournaments' },
    { name: 'Events', href: '/events' },
    { name: 'Galerie', href: '/gallery' },
    { name: 'Über uns', href: '/about' },
    { name: 'Kontakt', href: '/contact' },
  ];
  const [search, setSearch] = useState('');

  return (
    <header className="fixed top-0 w-full bg-slate-900/95 backdrop-blur-sm z-50 border-b border-amber-500/20">
      <div className="container mx-auto px-4">
        <div className="flex items-center h-20 relative">
          {/* Logo - positioned at far left with preserved aspect ratio */}
          <Link to="/" className={`flex items-center relative z-20 flex-shrink-0 w-fit h-fit transition-opacity duration-300 ${
            isMenuOpen ? 'opacity-30' : 'opacity-100'
          }`}>
            <picture>
              <source 
                media="(max-width: 768px)" 
                srcSet="/lovable-uploads/dc823777-063f-436c-b4fa-108a6a9e5cab.png 200w" 
                width="200" 
                height="78"
              />
              <source 
                media="(min-width: 769px)" 
                srcSet="/lovable-uploads/dc823777-063f-436c-b4fa-108a6a9e5cab.png 309w" 
                width="309" 
                height="120"
              />
              <img 
                src="/lovable-uploads/dc823777-063f-436c-b4fa-108a6a9e5cab.png" 
                alt="ASC Fechten Wien Logo" 
                className="h-auto max-h-[120px] w-auto object-contain relative z-30 mt-12 md:mt-16"
                width="309"
                height="120"
                loading="eager"
                sizes="(max-width: 768px) 200px, 309px"
                style={{ maxWidth: '309px', height: 'auto' }}
              />
            </picture>
          </Link>

          {/* Desktop Navigation - with overflow hidden to prevent wrapping */}
          <div className="hidden lg:flex items-center flex-1 ml-8 xl:ml-16 overflow-hidden">
            <nav className="flex items-center space-x-6 xl:space-x-8 flex-shrink min-w-0">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative text-sm font-medium transition-colors hover:text-amber-400 whitespace-nowrap flex-shrink-0 ${
                    location.pathname === item.href
                      ? 'text-amber-400'
                      : 'text-slate-300'
                  }`}
                >
                  {item.name}
                  {location.pathname === item.href && (
                    <div className="absolute -bottom-6 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent"></div>
                  )}
                </Link>
              ))}
            </nav>
            
            {/* Social + Search */}
            <div className="flex items-center gap-3 ml-auto pl-4 border-l border-amber-500/20 flex-shrink-0">
              {/* Responsive Search - Icon on smaller screens, full input on larger screens */}
              <div className="relative">
                {isSearchExpanded ? (
                  <form onSubmit={(e)=>{e.preventDefault(); if(search.trim()){ window.location.href = `/search?q=${encodeURIComponent(search.trim())}`; setIsSearchExpanded(false);}}}>
                    <input
                      aria-label="Suche"
                      value={search}
                      onChange={(e)=>setSearch(e.target.value)}
                      onBlur={() => setIsSearchExpanded(false)}
                      placeholder="Suche..."
                      className="px-3 py-1 rounded bg-slate-800 text-slate-200 placeholder-slate-400 border border-amber-500/20 focus:outline-none focus:ring-1 focus:ring-amber-500/50 w-48"
                      autoFocus
                    />
                  </form>
                ) : (
                  <>
                    {/* Full search input on larger screens */}
                    <form onSubmit={(e)=>{e.preventDefault(); if(search.trim()){ window.location.href = `/search?q=${encodeURIComponent(search.trim())}`}}} className="hidden xl:flex">
                      <input
                        aria-label="Suche"
                        value={search}
                        onChange={(e)=>setSearch(e.target.value)}
                        placeholder="Suche..."
                        className="px-3 py-1 rounded bg-slate-800 text-slate-200 placeholder-slate-400 border border-amber-500/20 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                      />
                    </form>
                    {/* Search icon on smaller screens */}
                    <button
                      onClick={() => setIsSearchExpanded(true)}
                      className="xl:hidden text-slate-400 hover:text-amber-400 transition-colors p-1"
                      aria-label="Suche öffnen"
                    >
                      <Search size={18} />
                    </button>
                  </>
                )}
              </div>
              <a
                href="https://www.facebook.com/people/ASC-Fechten/100057153112929/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-blue-400 transition-colors"
                aria-label="ASC Fechten Wien auf Facebook besuchen"
              >
                <Facebook size={18} />
              </a>
              <a
                href="https://www.instagram.com/asc_fechten/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-pink-400 transition-colors"
                aria-label="ASC Fechten Wien auf Instagram besuchen"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-red-400 transition-colors"
                aria-label="ASC Fechten Wien auf YouTube besuchen"
              >
                <Youtube size={18} />
              </a>
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-slate-300 hover:text-amber-400 transition-colors relative z-10 ml-auto"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-amber-500/20 relative z-40 bg-slate-900/98 backdrop-blur-sm">
            <nav className="py-4 space-y-2 pt-6">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-2 text-sm font-medium transition-colors hover:text-amber-400 hover:bg-slate-800/50 rounded relative z-50 ${
                    location.pathname === item.href
                      ? 'text-amber-400 bg-slate-800/30'
                      : 'text-slate-300'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Social Media Icons */}
              <div className="flex items-center justify-center space-x-6 pt-4 border-t border-amber-500/20 mt-4">
                <a
                  href="https://www.facebook.com/people/ASC-Fechten/100057153112929/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-blue-400 transition-colors"
                >
                  <Facebook size={24} />
                </a>
                <a
                  href="https://www.instagram.com/asc_fechten/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-pink-400 transition-colors"
                >
                  <Instagram size={24} />
                </a>
                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-red-400 transition-colors"
                >
                  <Youtube size={24} />
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
